import React, { useState } from "react";
import styles from "./index.module.scss";

import { concatAddress } from "@/utils/concat-address";
import ModularCloseBtn from "@/features/mod-perms/components/close-btn/close-btn";
import {
  GrantedPermission,
  type CheckboxPermission,
} from "@/types/modular-permissions.types";
import { useModularPermissionsStore } from "@/features/mod-perms/stores/modular-permissions.store";
import BlessModal from "@/components/layout/modal/bless-modal";
import useModularPermissionsManager from "@/features/mod-perms/hooks/useModularPermissions.hook";
import {
  PERMISSION_LIST,
  PERMISSION_MOD_SELECT_ALL_ID,
  REMOVE_SINGLE_PERM_DIALOGUE,
} from "@/constants/mod-perms-tool.constants";
import chunkArray from "@/utils/chunkArray";
import classNames from "classnames";
import { useAccount } from "wagmi";
import MobilePermsBtn from "@/features/mod-perms/components/cards/permissions-card/mobile-perms-btn/mobile-perms-btn";
import TelegramSVG from "@/features/mod-perms/components/cards/permissions-card/telegram-svg";
import WalletSVG from "@/features/mod-perms/components/cards/permissions-card/wallet-svg";
import PermissionCheckbox from "@/features/mod-perms/components/checkbox/permission-checkbox";
import { CgClose } from "react-icons/cg";
import ConfirmWindow from "@/features/mod-perms/components/modal/confirm-window";
import Tag from "@/features/mod-perms/components/cards/permissions-card/tag/tag";
import { Tooltip } from "antd";

type Props = {
  delegate: GrantedPermission;
  hideExitBtn?: boolean;
};

const PermissionsCard = ({ delegate, hideExitBtn = false }: Props) => {
  const { permModRecords, isDelegateContextGranted } =
    useModularPermissionsStore();
  const { contractClient, actions } = useModularPermissionsManager();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isConnected } = useAccount();

  const displayAddress = isDelegateContextGranted()
    ? delegate.delegate
    : delegate.vault;
  const permissionColumns = chunkArray(PERMISSION_LIST) as Array<
    CheckboxPermission[]
  >;

  function destroySelf() {
    setIsDeleting(true);
    setOpen(false);
    setTimeout(() => {
      actions.destroyDelegate(delegate.delegate);
    }, 300);
  }

  const permModCount = permModRecords.reduce((acc, rec) => {
    if (rec.delegate === delegate.delegate) {
      return acc + rec.ids.length;
    }
    return acc;
  }, 0);

  return (
    <div
      key={delegate.delegate + "granted"}
      className={classNames(
        styles.delegateCardWrapper,
        isDeleting && styles.isDeleting,
      )}
    >
      <div className={styles.delegateCard}>
        {!hideExitBtn && (
          <div className={styles.closeBtn}>
            <ModularCloseBtn
              aria-label="Remove"
              onClick={() => setOpen(true)}
              color="dark"
            />
          </div>
        )}
        <div
          className={styles.profileImage}
          style={{ backgroundColor: delegate.backgroundColor }}
        >
          {delegate.platform == "telegram" && <TelegramSVG />}
          {delegate.platform != "telegram" && <WalletSVG />}
          <span className={styles.walletAddress}>
            <Tooltip
              overlayStyle={{ whiteSpace: "nowrap", fontSize: 12 }}
              overlayInnerStyle={{ width: "fit-content" }}
              title={displayAddress}
            >
              {concatAddress(displayAddress)}
            </Tooltip>
          </span>
        </div>

        <div className={styles.mobileLayout}>
          <div className={styles.top}>
            <div className={styles.details}>
              <Tooltip title={displayAddress}>
                <p className={styles.name}>{concatAddress(displayAddress)}</p>
              </Tooltip>
              {/* <p className={styles.address}>{formatAddress(displayAddress)}</p> */}
            </div>
            <div className={styles.btnContainer}>
              <MobilePermsBtn delegate={delegate} />
            </div>
          </div>
          <div className={styles.bottom}>
            <Tag
              indicatorValue={
                delegate.allPermissionsEnabled
                  ? "All"
                  : delegate.permissionIds.length
              }
              title="Permissions selected"
            />
            {!!permModCount && (
              <Tag
                indicatorValue={permModCount}
                title="Pending"
                color="warning"
              />
            )}
          </div>
        </div>

        <div className={styles.permissionList}>
          {permissionColumns.map((permGroup, index) => (
            <div key={index} className={styles.permissionColumn}>
              {permGroup.map((permission) => {
                const isPermModified = permModRecords.some(
                  (record) =>
                    record.ids.includes(permission.id) &&
                    record.delegate === delegate.delegate,
                );
                return (
                  <div key={permission.id} className={styles.permissionTab}>
                    <PermissionCheckbox
                      label={permission.name}
                      permissionId={permission.id}
                      walletAddress={delegate.delegate}
                      isPermModified={isPermModified}
                      isChecked={delegate.permissionIds.includes(permission.id)}
                      isAllPermissionsToggle={
                        permission.id === PERMISSION_MOD_SELECT_ALL_ID
                      }
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <BlessModal
        open={open}
        closeIcon={<CgClose size={18} />}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <ConfirmWindow
          header="Are you sure?"
          paragraph={REMOVE_SINGLE_PERM_DIALOGUE}
          onCancel={() => setOpen(false)}
          onConfirm={() =>
            contractClient.removeDelegation(
              delegate.vault,
              delegate.delegate,
              destroySelf,
            )
          }
        />
      </BlessModal>
    </div>
  );
};

export default PermissionsCard;
